import React, { useState, useEffect, useRef } from "react";
import { 
  Button, 
  Card, 
  CardBody, 
  Chip, 
  Divider, 
  Input, 
  Switch,
  Tabs,
  Tab,
  Slider,
  Tooltip,
  Badge,
  Spinner
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowsAltH, 
  faColumns, 
  faEye, 
  faGripVertical, 
  faSave, 
  faSearch, 
  faFilter, 
  faUndo,
  faTimes,
  faCode,
  faTable,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,


} from "@fortawesome/free-solid-svg-icons";
import { getSubheadingStructure } from "../../utils/formGrouping.utils";
import { getJSonSchemaFromFieldInfoList } from "../../utils/frombuilder.utils";
import EnhancedPreviewForm from "./EnhancedPreviewForm";

const FormFieldArranger = ({ 
  fieldInfoList, 
  onSaveArrangement, 
  isOpen, 
  onOpenChange, 
  preferredLayout = "2-column",
  formData = {}
}) => {
  const [arrangedFields, setArrangedFields] = useState([]);
  const [layoutMode, setLayoutMode] = useState(preferredLayout);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyRequired, setShowOnlyRequired] = useState(false);
  const [groupBySubheading, setGroupBySubheading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [activeTab, setActiveTab] = useState("arrangement");
  const [fieldSizes, setFieldSizes] = useState({});
  const [showValidation, setShowValidation] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  // Initialize field sizes and arrangement
  useEffect(() => {
    if (fieldInfoList && fieldInfoList.length > 0) {
      try {
        const fieldsWithOrder = fieldInfoList.map((field, index) => ({
          ...field,
          visualOrder: field.displayOrder !== undefined ? field.displayOrder : index,
          originalOrder: field.displayOrder !== undefined ? field.displayOrder : index,
          size: fieldSizes[field.id] || "medium"
        }));
        setArrangedFields(fieldsWithOrder);
        
        // Initialize default field sizes
        const defaultSizes = {};
        fieldInfoList.forEach(field => {
          if (!fieldSizes[field.id]) {
            if (field.Type === "textarea" || field.Type === "file") {
              defaultSizes[field.id] = "large";
            } else if (field.Type === "boolean" || field.Type === "slider") {
              defaultSizes[field.id] = "small";
            } else {
              defaultSizes[field.id] = "medium";
            }
          }
        });
        setFieldSizes(prev => ({ ...prev, ...defaultSizes }));
        setError(null);
      } catch (err) {
        console.error("Error initializing fields:", err);
        setError("Failed to initialize form fields. Please refresh and try again.");
      }
    }
  }, [fieldInfoList]);

  useEffect(() => {
    setLayoutMode(preferredLayout);
  }, [preferredLayout]);







  const handleDragStart = (e, index) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverItem(index);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    try {
      const newArrangedFields = [...arrangedFields];
      const draggedField = newArrangedFields[draggedItem];
      
      // Add null check for dragged field
      if (!draggedField) {
        console.error("Dragged field is undefined");
        return;
      }
      
      newArrangedFields.splice(draggedItem, 1);
      newArrangedFields.splice(index, 0, draggedField);
      
      // Update visualOrder for all fields after reordering
      newArrangedFields.forEach((field, idx) => {
        if (field) {
          field.visualOrder = idx;
          // Also update displayOrder to keep it in sync
          field.displayOrder = idx;
        }
      });
      
      setArrangedFields(newArrangedFields);
      setDraggedItem(null);
      setDragOverItem(null);
    } catch (err) {
      console.error("Error reordering fields:", err);
      setError("Failed to reorder fields. Please try again.");
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const resetToOriginal = () => {
    try {
      const originalOrder = [...fieldInfoList].filter(field => field && field.id).sort((a, b) => {
        // Sort by displayOrder if available, otherwise by original order
        if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
          return a.displayOrder - b.displayOrder;
        }
        return (a.originalOrder || 0) - (b.originalOrder || 0);
      });
      const fieldsWithOrder = originalOrder.map((field, index) => ({
        ...field,
        visualOrder: index,
        originalOrder: index,
        size: fieldSizes[field.id] || "medium"
      }));
      setArrangedFields(fieldsWithOrder);
      setError(null);
    } catch (err) {
      console.error("Error resetting fields:", err);
      setError("Failed to reset fields. Please try again.");
    }
  };

  const changeFieldSize = (fieldId, newSize) => {
    try {
      setFieldSizes(prev => ({ ...prev, [fieldId]: newSize }));
      setArrangedFields(prev => 
        prev.map(field => 
          field.id === fieldId ? { ...field, size: newSize } : field
        )
      );
    } catch (err) {
      console.error("Error changing field size:", err);
      setError("Failed to change field size. Please try again.");
    }
  };

  const saveArrangement = async () => {
    if (isSaving) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      const arrangementData = {
        fields: arrangedFields.filter(field => field && field.id && field.Title && field.Type).map(field => ({
          id: field.id,
          Title: field.Title,
          Type: field.Type,
          Required: field.Required,
          visualOrder: field.visualOrder,
          groupTitle: field.groupTitle,
          size: field.size,
          validation: field.validation || {}
        })),
        preferredLayout: layoutMode,
        fieldSizes: fieldSizes
      };
      
      await onSaveArrangement(arrangementData);
      onOpenChange(false);
    } catch (err) {
      console.error("Error saving arrangement:", err);
      setError("Failed to save arrangement. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Filter fields based on search and required filter
  const filteredFields = arrangedFields.filter(field => {
    // Add null checks to prevent errors
    if (!field || !field.Title || !field.Type) {
      return false; // Skip invalid fields
    }
    
    const matchesSearch = field.Title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         field.Type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRequired = !showOnlyRequired || field.Required;
    return matchesSearch && matchesRequired;
  });

  // Group fields by subheading if enabled
  const groupedFields = groupBySubheading 
    ? filteredFields.reduce((groups, field) => {
        const groupName = field.groupTitle || "Ungrouped Fields";
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(field);
        return groups;
      }, {})
    : { "All Fields": filteredFields };

  const getFieldIcon = (type) => {
    if (!type) return "❓";
    
    const iconMap = {
      text: "📝",
      textarea: "📄",
      integer: "🔢",
      boolean: "☑️",
      date: "📅",
      file: "📎",
      dropdown: "📋",
      relation: "🔗",
      formula: "🧮",
      subheading: "📁"
    };
    return iconMap[type] || "❓";
  };

  const getFieldTypeColor = (type) => {
    if (!type) return "default";
    
    const colorMap = {
      text: "primary",
      textarea: "secondary",
      integer: "success",
      boolean: "warning",
      date: "info",
      file: "danger",
      dropdown: "primary",
      relation: "secondary",
      formula: "success",
      subheading: "default"
    };
    return colorMap[type] || "default";
  };

  const getSizeLabel = (size) => {
    if (!size) return "M";
    
    const labelMap = {
      small: "S",
      medium: "M", 
      large: "L",
      full: "Full"
    };
    return labelMap[size] || "M";
  };

  // Generate preview data for form preview
  const generatePreviewData = () => {
    if (!fieldInfoList || fieldInfoList.length === 0) {
      return {
        Name: "Form Preview",
        JsonSchema: {
          properties: {},
          required: []
        }
      };
    }

    const jsonSchema = getJSonSchemaFromFieldInfoList(fieldInfoList, []);
    
    return {
      Name: formData.metricName || "Form Preview",
      description: formData.description || "Preview of your form as users will see it",
      JsonSchema: jsonSchema
    };
  };

  // Generate JSON structure data
  const generateJSONStructureData = () => {
    if (!fieldInfoList || fieldInfoList.length === 0) {
      return { structure: [], groupedData: {} };
    }

    const formSchema = fieldInfoList.map(field => [
      field.Title,
      {
        type: field.Type,
        headingLevel: field.headingLevel,
        subheadingDescription: field.subheadingDescription,
        required: field.Required,
        size: fieldSizes[field.id] || "medium"
      }
    ]);

    const structure = getSubheadingStructure(formSchema);
    
    // Create sample data for preview
    const sampleData = {};
    fieldInfoList.forEach(field => {
      if (field.Type !== "subheading") {
        switch (field.Type) {
          case "text":
          case "textarea":
            sampleData[field.Title] = `Sample ${field.Title}`;
            break;
          case "integer":
            sampleData[field.Title] = 123;
            break;
          case "boolean":
            sampleData[field.Title] = true;
            break;
          case "dropdown":
            sampleData[field.Title] = "Option 1";
            break;
          case "file":
            sampleData[field.Title] = ["file1.pdf"];
            break;
          default:
            sampleData[field.Title] = `Sample value`;
        }
      }
    });

    return { structure, sampleData };
  };

  const renderLayoutPreview = () => {
    if (layoutMode === "2-column") {
      return (
        <div className="grid grid-cols-2 gap-1 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
          {filteredFields.slice(0, 8).map((field, index) => (
            <div key={`preview-${field.id}`} className="text-xs">
              <div className="flex items-center gap-1">
                <span className="text-emerald-700 font-medium">#{field.visualOrder + 1}</span>
                <span className="font-medium truncate">{field.Title}</span>
                <span className="text-emerald-500 text-xs">{getSizeLabel(field.size)}</span>
              </div>
            </div>
          ))}
          {filteredFields.length > 8 && (
            <div className="col-span-2 text-center text-xs text-emerald-600">
              +{filteredFields.length - 8} more
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="space-y-1 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
          {filteredFields.slice(0, 6).map((field, index) => (
            <div key={`preview-${field.id}`} className="text-xs">
              <div className="flex items-center gap-1">
                <span className="text-emerald-700 font-medium">#{field.visualOrder + 1}</span>
                <span className="font-medium truncate">{field.Title}</span>
                <span className="text-emerald-500 text-xs">{getSizeLabel(field.size)}</span>
              </div>
            </div>
          ))}
          {filteredFields.length > 6 && (
            <div className="text-center text-xs text-emerald-600">
              +{filteredFields.length - 6} more
            </div>
          )}
        </div>
      );
    }
  };

  const renderFieldCard = (field, index) => {
    // Add null check to prevent errors
    if (!field || !field.id || !field.Title || !field.Type) {
      return null; // Skip invalid fields
    }
    
    const isDragging = draggedItem === index;
    const isDragOver = dragOverItem === index;
    
    return (
      <Card
        key={`${field.id}-${index}`}
        className={`
          cursor-move transition-all duration-200
          ${isDragging ? 'opacity-50 scale-95' : ''}
          ${isDragOver ? 'border-2 border-emerald-400 bg-emerald-50' : ''}
        `}
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDrop={(e) => handleDrop(e, index)}
        onDragEnd={handleDragEnd}
        shadow="sm"
      >
        <CardBody className="p-3">
          <div className="flex items-center gap-3">
            <div className="text-emerald-600">
              <FontAwesomeIcon icon={faGripVertical} className="text-sm" />
            </div>
            
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
              #{field.visualOrder + 1}
            </span>
            
            <div className="text-lg">
              {getFieldIcon(field.Type)}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 truncate">
                  {field.Title}
                </span>
                {field.Required && (
                  <Chip size="sm" color="danger" variant="flat">
                    *
                  </Chip>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Chip size="sm" color={getFieldTypeColor(field.Type)} variant="flat">
                  {field.Type}
                </Chip>
                {field.groupTitle && field.groupTitle !== "Ungrouped Fields" && (
                  <span className="text-gray-500">📁 {field.groupTitle}</span>
                )}
              </div>
            </div>
            
            {/* Field Size Control */}
            <div className="flex items-center gap-1">
              <Tooltip content={`Field width: ${field.size === 'small' ? '25%' : field.size === 'medium' ? '50%' : field.size === 'large' ? '75%' : '100%'} of column width`}>
                <div className="flex gap-1">
                  {["small", "medium", "large", "full"].map((size) => (
                    <Button
                      key={size}
                      size="sm"
                      variant={field.size === size ? "solid" : "bordered"}
                      color={field.size === size ? "primary" : "default"}
                      className="min-w-8 h-6 text-xs"
                      onPress={() => changeFieldSize(field.id, size)}
                    >
                      {getSizeLabel(size)}
                    </Button>
                  ))}
                </div>
              </Tooltip>
            </div>
          </div>

          {/* Validation Info */}
          {showValidation && field.Required && (
            <div className="mt-2 p-2 bg-emerald-50 rounded text-xs text-emerald-700 border border-emerald-200">
              <div className="flex items-center gap-1">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>Required field - validation will be enforced</span>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  // Render the appropriate content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case "arrangement":
        return (
          <div className="grid grid-cols-5 gap-6" style={{ height: 'calc(95vh - 300px)' }}>
            {/* Left Side - Layout Preview & Controls (Reduced width) */}
            <div className="col-span-2 space-y-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faEye} className="text-emerald-600" />
                  <span className="font-medium text-gray-800">Layout Preview</span>
                  <span className="text-xs text-gray-500">• Shows final form layout</span>
                </div>
                
                {/* Layout Mode Selection */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={layoutMode === "2-column" ? "solid" : "bordered"}
                    color={layoutMode === "2-column" ? "primary" : "default"}
                    onPress={() => setLayoutMode("2-column")}
                    startContent={<FontAwesomeIcon icon={faColumns} />}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    2-Column
                  </Button>
                  <Button
                    size="sm"
                    variant={layoutMode === "1-column" ? "solid" : "bordered"}
                    color={layoutMode === "1-column" ? "primary" : "default"}
                    onPress={() => setLayoutMode("1-column")}
                    startContent={<FontAwesomeIcon icon={faColumns} />}
                  >
                    1-Column
                  </Button>
                </div>

                {renderLayoutPreview()}
                
                {/* Size Legend - Compact */}
                <div className="text-xs text-gray-600">
                  <div className="font-medium text-gray-700 mb-1">Sizes: S(25%) M(50%) L(75%) Full(100%)</div>
                </div>



                {/* Quick Stats - Compact */}
                <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                  <div className="text-sm font-medium text-emerald-800 mb-2">Summary</div>
                  <div className="grid grid-cols-2 gap-1 text-xs text-emerald-700">
                    <div>Fields: {arrangedFields.length}</div>
                    <div>Required: {arrangedFields.filter(f => f && f.Required).length}</div>
                    <div>Layout: {layoutMode}</div>
                    <div>Groups: {Object.keys(groupedFields).length}</div>
                  </div>
                </div>

                {/* Validation Toggle - Compact */}
                <div className="flex items-center gap-2 text-sm">
                  <Switch
                    size="sm"
                    isSelected={showValidation}
                    onValueChange={setShowValidation}
                  />
                  <span>Show validations</span>
                </div>
              </div>
            </div>

            {/* Right Side - Field Arrangement (More space) */}
            <div className="col-span-3 space-y-4">
              <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faGripVertical} className="text-emerald-600" />
                <span className="font-medium text-gray-800">Rearrange Fields</span>
                <span className="text-xs text-gray-500 ml-2">• S/M/L/Full control field width within columns</span>
              </div>
                <Button
                  size="sm"
                  variant="bordered"
                  color="default"
                  onPress={resetToOriginal}
                  startContent={<FontAwesomeIcon icon={faUndo} />}
                >
                  Reset
                </Button>
              </div>

              {/* Search and Filters */}
              <div className="space-y-3">
                <Input
                  size="sm"
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  startContent={<FontAwesomeIcon icon={faSearch} className="text-gray-400" />}
                />
                <div className="flex items-center gap-4 text-xs">
                  <label className="flex items-center gap-2">
                    <Switch
                      size="sm"
                      isSelected={showOnlyRequired}
                      onValueChange={setShowOnlyRequired}
                    />
                    Show only required
                  </label>
                  <label className="flex items-center gap-2">
                    <Switch
                      size="sm"
                      isSelected={groupBySubheading}
                      onValueChange={setGroupBySubheading}
                    />
                    Group by section
                  </label>
                </div>
              </div>

              {/* Fields List - Better scrolling */}
              <div className="space-y-3 overflow-y-auto pr-2" style={{ maxHeight: 'calc(95vh - 400px)' }}>
                {Object.entries(groupedFields).map(([groupName, fields]) => (
                  <div key={groupName}>
                    {groupBySubheading && (
                      <div className="text-xs font-medium text-emerald-600 mb-2 px-2">
                        📁 {groupName} ({fields.length})
                      </div>
                    )}
                    <div className="space-y-2">
                      {fields.map((field, index) => {
                        const originalIndex = arrangedFields.findIndex(f => f.id === field.id);
                        return renderFieldCard(field, originalIndex);
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      case "preview":
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faEye} className="text-emerald-600" />
              <span className="text-base font-medium">Form Preview</span>
            </div>
            <EnhancedPreviewForm 
              data={generatePreviewData()} 
              fieldInfoList={fieldInfoList} 
            />
          </div>
        );
      
      case "structure":
        const { structure, sampleData } = generateJSONStructureData();
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faTable} className="text-emerald-600" />
              <span className="text-base font-medium">JSON Structure</span>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Form Structure */}
              <div>
                <h4 className="text-base font-medium mb-2">Form Structure</h4>
                <div className="bg-gray-50 p-3 rounded-lg max-h-80 overflow-y-auto">
                  {structure.length > 0 ? (
                    structure.map((group, index) => (
                      <div key={index} className="mb-4 last:mb-0">
                        <div className="font-medium text-emerald-600 mb-1">
                          📁 {group.subheading.title}
                        </div>
                        {group.subheading.description && (
                          <div className="text-sm text-gray-500 mb-2">
                            {group.subheading.description}
                          </div>
                        )}
                        <div className="ml-4 space-y-1">
                          {group.fields.map((field, fieldIndex) => (
                            <div key={fieldIndex} className="text-sm">
                              • {field.key} ({field.config.type})
                              {field.config.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                              <Chip size="sm" color="primary" variant="flat" className="ml-2">
                                {field.config.size}
                              </Chip>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-center py-8">
                      No fields added yet
                    </div>
                  )}
                </div>
              </div>

              {/* Sample Data */}
              <div>
                <h4 className="text-base font-medium mb-2">Sample Data</h4>
                <div className="bg-gray-900 text-green-400 p-3 rounded-lg max-h-80 overflow-y-auto">
                  <pre className="text-xs">
                    {JSON.stringify(sampleData, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => onOpenChange(false)}
      />
      
      {/* Centered Modal */}
      <div 
        ref={containerRef}
        className="relative bg-white shadow-2xl transform transition-all duration-300 ease-in-out rounded-lg overflow-hidden"
        style={{ 
          width: '1400px',
          maxWidth: '95vw',
          maxHeight: '95vh'
        }}
      >


        {/* Header - Compact */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faArrowsAltH} className="text-emerald-600 text-base" />
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Preview & Arrange</h2>
              <p className="text-xs text-gray-600">
                Arrange fields, preview form, and view structure
              </p>
            </div>
          </div>
          <Button
            isIconOnly
            variant="light"
            onPress={() => onOpenChange(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>

        {/* Error Display - Compact */}
        {error && (
          <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-sm" />
              <span className="text-xs">{error}</span>
            </div>
          </div>
        )}

        {/* Tabs - Compact */}
        <div className="px-4 pt-2">
          <Tabs 
            aria-label="Preview and arrangement options" 
            selectedKey={activeTab}
            onSelectionChange={setActiveTab}
            variant="light"
            classNames={{
              tabList: "gap-2 w-full relative rounded-none p-1 border-b border-divider",
              cursor: "w-full bg-emerald-100",
              tab: "max-w-fit px-3 h-8 text-sm",
              tabContent: "group-data-[selected=true]:text-emerald-700"
            }}
          >
            <Tab key="arrangement" title="Arrange Fields">
              <div className="mt-2">
                {renderTabContent()}
              </div>
            </Tab>
            <Tab key="preview" title="Form Preview">
              <div className="mt-2">
                {renderTabContent()}
              </div>
            </Tab>
            <Tab key="structure" title="JSON Structure">
              <div className="mt-2">
                {renderTabContent()}
              </div>
            </Tab>
          </Tabs>
        </div>

        {/* Body - Compact */}
        <div className="px-4 pb-16 overflow-y-auto" style={{ height: 'calc(95vh - 180px)' }}>
          {activeTab === "arrangement" && (
            <>
              {/* Instructions - Compact */}
              <div className="bg-emerald-50 p-3 rounded-lg mt-4 border border-emerald-200">
                <h5 className="font-medium text-emerald-800 mb-2 text-sm">Quick Tips:</h5>
                <ul className="text-xs text-emerald-700 space-y-1">
                  <li>• Drag fields to reorder • S/M/L/Full = field width (25%/50%/75%/100%)</li>
                  <li>• Use layout buttons for 1/2-column preview • Search to find fields</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer - Only show for arrangement tab */}
        {activeTab === "arrangement" && (
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="bordered"
                onPress={() => onOpenChange(false)}
                className="px-6"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={saveArrangement}
                startContent={isSaving ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faSave} />}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Arrangement & Layout"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormFieldArranger;
